import { useState } from 'react';
import {
  Mail,
  Settings,
  Send,
  FileText,
  CheckCircle,
  AlertCircle,
  Edit2,
  Eye,
  Save,
  X,
  TestTube,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  description: string;
  lastUpdated: Date;
}

interface SMTPConfig {
  host: string;
  port: number;
  username: string;
  password: string;
  encryption: 'tls' | 'ssl' | 'none';
  fromName: string;
  fromEmail: string;
}

// Demo data
const emailTemplates: EmailTemplate[] = [
  { id: '1', name: 'Hosgeldin', subject: 'Bosphorus Labs\'a Hosgeldiniz', description: 'Yeni uye kayit e-postasi', lastUpdated: new Date() },
  { id: '2', name: 'Sifre Sifirlama', subject: 'Sifre Sifirlama Talebi', description: 'Sifre sifirlama linki', lastUpdated: new Date() },
  { id: '3', name: 'Proje Onay', subject: 'Projeniz Onaylandi', description: 'Proje talebi kabul e-postasi', lastUpdated: new Date() },
  { id: '4', name: 'Iletisim Yanit', subject: 'Mesajiniz Alindi', description: 'Iletisim formu otomatik yanit', lastUpdated: new Date() },
  { id: '5', name: 'Newsletter', subject: 'Aylik Bulten', description: 'Aylik haber bulteni', lastUpdated: new Date() },
];

const initialConfig: SMTPConfig = {
  host: 'smtp.example.com',
  port: 587,
  username: 'noreply@bosphoruslabs.com',
  password: '********',
  encryption: 'tls',
  fromName: 'Bosphorus Labs',
  fromEmail: 'noreply@bosphoruslabs.com',
};

export function AdminEmail() {
  const [config, setConfig] = useState<SMTPConfig>(initialConfig);
  const [editingConfig, setEditingConfig] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);

  const handleTestEmail = () => {
    if (!testEmail) return;
    setTestStatus('sending');
    setTimeout(() => {
      setTestStatus('success');
      setTimeout(() => setTestStatus('idle'), 3000);
    }, 2000);
  };

  const handleSaveConfig = () => {
    setEditingConfig(false);
    // Save logic here
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">E-posta Ayarlari</h1>
        <p className="text-text-secondary">SMTP yapilandirmasi ve e-posta sablonlarini yonetin</p>
      </div>

      {/* SMTP Configuration */}
      <div className="bg-bg-card border border-border-default rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Settings size={20} className="text-accent" />
            <div>
              <h2 className="font-semibold text-text-primary">SMTP Ayarlari</h2>
              <p className="text-sm text-text-secondary">E-posta sunucu yapilandirmasi</p>
            </div>
          </div>
          {!editingConfig && (
            <button
              onClick={() => setEditingConfig(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90"
            >
              <Edit2 size={16} /> Duzenle
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-text-tertiary mb-1 block">SMTP Sunucu</label>
            <input
              type="text"
              value={config.host}
              onChange={(e) => setConfig({ ...config, host: e.target.value })}
              disabled={!editingConfig}
              className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none disabled:opacity-50"
            />
          </div>
          <div>
            <label className="text-xs text-text-tertiary mb-1 block">Port</label>
            <input
              type="number"
              value={config.port}
              onChange={(e) => setConfig({ ...config, port: parseInt(e.target.value) })}
              disabled={!editingConfig}
              className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none disabled:opacity-50"
            />
          </div>
          <div>
            <label className="text-xs text-text-tertiary mb-1 block">Kullanici Adi</label>
            <input
              type="text"
              value={config.username}
              onChange={(e) => setConfig({ ...config, username: e.target.value })}
              disabled={!editingConfig}
              className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none disabled:opacity-50"
            />
          </div>
          <div>
            <label className="text-xs text-text-tertiary mb-1 block">Sifre</label>
            <input
              type="password"
              value={config.password}
              onChange={(e) => setConfig({ ...config, password: e.target.value })}
              disabled={!editingConfig}
              className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none disabled:opacity-50"
            />
          </div>
          <div>
            <label className="text-xs text-text-tertiary mb-1 block">Sifreleme</label>
            <select
              value={config.encryption}
              onChange={(e) => setConfig({ ...config, encryption: e.target.value as SMTPConfig['encryption'] })}
              disabled={!editingConfig}
              className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none disabled:opacity-50"
            >
              <option value="tls">TLS</option>
              <option value="ssl">SSL</option>
              <option value="none">Yok</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-text-tertiary mb-1 block">Gonderici Adi</label>
            <input
              type="text"
              value={config.fromName}
              onChange={(e) => setConfig({ ...config, fromName: e.target.value })}
              disabled={!editingConfig}
              className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none disabled:opacity-50"
            />
          </div>
        </div>

        {editingConfig && (
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setEditingConfig(false)}
              className="px-4 py-2 rounded-lg border border-border-default text-text-secondary hover:text-text-primary"
            >
              Iptal
            </button>
            <button
              onClick={handleSaveConfig}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90"
            >
              <Save size={16} /> Kaydet
            </button>
          </div>
        )}
      </div>

      {/* Test Email */}
      <div className="bg-bg-card border border-border-default rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <TestTube size={20} className="text-accent" />
          <div>
            <h2 className="font-semibold text-text-primary">Test E-postasi</h2>
            <p className="text-sm text-text-secondary">SMTP yapilandirmanizi test edin</p>
          </div>
        </div>

        <div className="flex gap-4">
          <input
            type="email"
            placeholder="test@email.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="flex-1 px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
          />
          <button
            onClick={handleTestEmail}
            disabled={testStatus === 'sending'}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors",
              testStatus === 'sending' ? 'bg-accent/50' :
              testStatus === 'success' ? 'bg-green-500' :
              testStatus === 'error' ? 'bg-red-500' : 'bg-accent hover:bg-accent/90'
            )}
          >
            {testStatus === 'sending' ? (
              <>Gonderiliyor...</>
            ) : testStatus === 'success' ? (
              <><CheckCircle size={16} /> Gonderildi</>
            ) : testStatus === 'error' ? (
              <><AlertCircle size={16} /> Hata</>
            ) : (
              <><Send size={16} /> Test Gonder</>
            )}
          </button>
        </div>
      </div>

      {/* Email Templates */}
      <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
        <div className="p-4 border-b border-border-default flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText size={20} className="text-accent" />
            <h2 className="font-semibold text-text-primary">E-posta Sablonlari</h2>
          </div>
          <span className="text-sm text-text-tertiary">{emailTemplates.length} sablon</span>
        </div>
        <div className="divide-y divide-border-default">
          {emailTemplates.map(template => (
            <div
              key={template.id}
              className="p-4 flex items-center justify-between hover:bg-bg-elevated/50 cursor-pointer"
              onClick={() => setSelectedTemplate(template)}
            >
              <div>
                <p className="font-medium text-text-primary">{template.name}</p>
                <p className="text-sm text-text-secondary">{template.description}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-text-tertiary">
                  {template.lastUpdated.toLocaleDateString('tr-TR')}
                </span>
                <button className="p-2 rounded hover:bg-bg-elevated text-text-tertiary hover:text-accent">
                  <Edit2 size={16} />
                </button>
                <button className="p-2 rounded hover:bg-bg-elevated text-text-tertiary hover:text-accent">
                  <Eye size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-accent">1,234</p>
          <p className="text-sm text-text-secondary">Bu Ay Gonderilen</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-500">98.5%</p>
          <p className="text-sm text-text-secondary">Teslim Orani</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-500">42%</p>
          <p className="text-sm text-text-secondary">Acilma Orani</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-purple-500">12%</p>
          <p className="text-sm text-text-secondary">Tiklama Orani</p>
        </div>
      </div>
    </div>
  );
}
